// The website pages card (the principal's pause exception of 8 October 2026, item 4): Cleo asks to publish several
// pages of his website at once (neucleos_canvas canvas-public-publish-pages), and the card shows, in his words, each
// path as changed (every character removed and added, and a warning when a change touches the page's code), added or
// unchanged, and every other path as kept. The World reads the pages fresh for the exact request
// (canvas-public-pages-review); Approve waits until that read matches the request, and the World approves this step
// only from a Desk that shows this card (backend lib/gateCatalog.js CARD_APPROVED_STEP_KEYS).
import { canvasDoor } from "./canvasShareClient";

export const WEBSITE_PAGES_ACTION = "canvas-public-publish-pages";

/** The exact set the call names: what the card asks the World about. */
export interface WebsitePagesRef {
  readonly siteKey: string;
  readonly expectedSequence: number;
  readonly requestKey: string;
  readonly pages: ReadonlyArray<{
    readonly canvasId: string;
    readonly sequence: number;
    readonly captureDigest: string;
    readonly path: string;
    readonly indexing: "indexed" | "unlisted";
  }>;
}
export interface WebsitePageEdit {
  readonly before: string;
  readonly removed: string;
  readonly added: string;
  readonly after: string;
  readonly code: boolean;
}
export interface WebsitePageRow {
  readonly path: string;
  readonly canvasId: string;
  readonly sequence: number;
  readonly title: string | null;
  readonly indexing: string;
  readonly captureDigest: string;
  readonly status: "changed" | "added" | "unchanged";
  readonly edits: ReadonlyArray<WebsitePageEdit> | null;
  readonly tooLarge: boolean;
  /** An added page whole, and whether it may hold code: its count and its exact text. */
  readonly addedPage?: { readonly text: string; readonly characters: number; readonly code: boolean };
}
export type WebsitePagesRead =
  | { readonly status: "reading" }
  | { readonly status: "decided" }
  | {
      readonly status: "loaded";
      readonly hostname: string;
      readonly sequence: number;
      readonly pages: ReadonlyArray<WebsitePageRow>;
      readonly kept: ReadonlyArray<string>;
      readonly files: number;
      readonly redirects: number;
    }
  | { readonly status: "failed"; readonly reason: string };

export const WEBSITE_PAGES_UNREAD =
  "Neucleos could not read the pages this publication changes, so it cannot be approved here.";

const DIGEST = /^[0-9a-f]{64}$/;
/** The set a canvas-public-publish-pages call names, or null when it is not one this card can show exactly. */
export function websitePagesRefOf(input: Record<string, unknown>): WebsitePagesRef | null {
  const { siteKey, expectedSequence, requestKey, pages } = input;
  if (
    typeof siteKey !== "string" ||
    typeof requestKey !== "string" ||
    typeof expectedSequence !== "number"
  )
    return null;
  if (
    !Number.isSafeInteger(expectedSequence) ||
    !Array.isArray(pages) ||
    pages.length < 1 ||
    pages.length > 8
  )
    return null;
  const rows: Array<WebsitePagesRef["pages"][number]> = [];
  for (const page of pages) {
    if (typeof page !== "object" || page === null) return null;
    const row = page as Record<string, unknown>;
    if (
      typeof row["canvasId"] !== "string" ||
      typeof row["path"] !== "string" ||
      typeof row["captureDigest"] !== "string"
    )
      return null;
    if (
      typeof row["sequence"] !== "number" ||
      !Number.isSafeInteger(row["sequence"]) ||
      !DIGEST.test(row["captureDigest"])
    )
      return null;
    if (row["indexing"] !== "indexed" && row["indexing"] !== "unlisted") return null;
    // A page with view state would capture differently from what this card reads: it is not shown here.
    if (row["state"] !== undefined) return null;
    rows.push({
      canvasId: row["canvasId"],
      sequence: row["sequence"],
      captureDigest: row["captureDigest"],
      path: row["path"],
      indexing: row["indexing"],
    });
  }
  return { siteKey, expectedSequence, requestKey, pages: rows };
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");
function rowOf(value: unknown): WebsitePageRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  const status = row["status"];
  if (status !== "changed" && status !== "added" && status !== "unchanged") return null;
  if (
    typeof row["path"] !== "string" ||
    typeof row["canvasId"] !== "string" ||
    typeof row["sequence"] !== "number" ||
    typeof row["captureDigest"] !== "string"
  )
    return null;
  const change = (row["change"] ?? null) as Record<string, unknown> | null;
  const tooLarge = change?.["tooLarge"] === true;
  const edits = Array.isArray(change?.["edits"])
    ? (change["edits"] as unknown[]).map((edit) => {
        const e = (edit ?? {}) as Record<string, unknown>;
        return {
          before: text(e["before"]),
          removed: text(e["removed"]),
          added: text(e["added"]),
          after: text(e["after"]),
          code: e["code"] === true,
        };
      })
    : null;
  if (status === "changed" && !tooLarge && (edits === null || edits.length === 0)) return null;
  const n = (row["addedPage"] ?? null) as Record<string, unknown> | null;
  const addedPage =
    n && typeof n["text"] === "string" && typeof n["characters"] === "number"
      ? { text: n["text"], characters: n["characters"], code: n["code"] === true }
      : null;
  if (status === "added" && addedPage === null && row["addedPage"] !== undefined) return null;

  return {
    path: row["path"],
    canvasId: row["canvasId"],
    sequence: row["sequence"],
    title: typeof row["title"] === "string" ? row["title"] : null,
    indexing: text(row["indexing"]),
    captureDigest: row["captureDigest"],
    ...(addedPage ? { addedPage } : {}),
    status,
    edits,
    tooLarge,
  };
}

/** The pages this exact set changes, adds and keeps, as the World reads them now. */
export async function readWebsitePages(
  worldKey: string,
  ref: WebsitePagesRef,
): Promise<WebsitePagesRead> {
  const read = await canvasDoor(worldKey, "canvas-public-pages-review", { ...ref });
  if (!read.ok)
    return {
      status: "failed",
      reason: read.reason ? `${WEBSITE_PAGES_UNREAD} ${read.reason}` : WEBSITE_PAGES_UNREAD,
    };
  const body = read.body;
  if (body["decided"] === true) return { status: "decided" };
  const pages = Array.isArray(body["pages"]) ? body["pages"].map(rowOf) : null;
  const kept = Array.isArray(body["kept"])
    ? body["kept"].map((row) => (row as Record<string, unknown>)?.["path"])
    : null;
  if (
    typeof body["hostname"] !== "string" ||
    typeof body["sequence"] !== "number" ||
    !pages ||
    pages.some((row) => row === null) ||
    !kept ||
    kept.some((path) => typeof path !== "string")
  )
    return { status: "failed", reason: WEBSITE_PAGES_UNREAD };
  return {
    status: "loaded",
    hostname: body["hostname"],
    sequence: body["sequence"],
    pages: pages as WebsitePageRow[],
    kept: kept as string[],
    files: typeof body["files"] === "number" ? body["files"] : 0,
    redirects: typeof body["redirects"] === "number" ? body["redirects"] : 0,
  };
}

/** Why the card cannot be approved as read, or null: the read must be the exact set the call names, now. */
export function websitePagesBlocked(ref: WebsitePagesRef, read: WebsitePagesRead): string | null {
  if (read.status === "reading") return "Reading the pages this publication changes…";
  if (read.status === "failed") return read.reason;
  if (read.status === "decided") return "This publication has already been decided.";
  if (read.sequence !== ref.expectedSequence)
    return "The website changed since Cleo prepared these pages; ask her again.";
  const asked = ref.pages
    .map(
      (page) =>
        `${page.path}|${page.canvasId}|${page.sequence}|${page.captureDigest}|${page.indexing}`,
    )
    .sort();
  const shown = read.pages
    .map(
      (page) =>
        `${page.path}|${page.canvasId}|${page.sequence}|${page.captureDigest}|${page.indexing}`,
    )
    .sort();
  if (asked.join("\n") !== shown.join("\n"))
    return "The pages read are not the ones Cleo asked to publish; ask her again.";
  if (read.pages.some((page) => page.status === "added" && !page.addedPage))
    return "Neucleos could not read a new page this publication adds in full, so it cannot be approved here.";
  if (read.pages.some((page) => page.tooLarge))
    return "A page changes too much to show every change on this card, so it cannot be approved here.";
  return null;
}

// A quoted piece of a page never starts a line of its own on the card (#1879 count review: page text must never stand
// in for the card's own rows): its line breaks are shown as ⏎.
const quote = (value: string) => (value === "" ? "nothing" : `“${value.replace(/\r\n|\r|\n|\u2028|\u2029/g, " ⏎ ")}”`);
/** One page's row on the card: what it is, and every change in full. */
export function websitePageWords(page: WebsitePageRow): readonly [label: string, text: string] {
  const named = `${page.title ? `“${page.title}”` : "A page"}, version ${page.sequence}${page.indexing === "unlisted" ? " (unlisted)" : ""}`;
  if (page.status === "added")
    return [`Adds ${page.path}`, `${named}, a new page at this address.`];
  if (page.status === "unchanged")
    return [`Republishes ${page.path}`, `${named}, exactly as it is now.`];
  if (page.tooLarge) return [`Changes ${page.path}`, `${named}. It changes too much to show here.`];
  return [`Changes ${page.path}`, `${named}. ${exactChanges(page).join(" ")}`];
}
/**
 * A page's row on the card, in plain words (the Orchestrator's ruling, 9 Oct 2026, after rehearsal run 12: plain words
 * first, raw HTML never the card's face): the page by name and address, then, for a changed page, its exact count of
 * changes from the World's character comparison (which never leaves one out) and whether any touches the page's code;
 * a new page by its size; a page republished as it is. The exact pieces are under "Show exact changes" (websitePagesExact).
 */
export function websitePageRows(page: WebsitePageRow): Array<readonly [label: string, text: string]> {
  // A Canvas title is the author's text: quoted, with angle brackets shown as ‹ ›, so no markup is ever the card's face.
  const label = `${page.title ? `“${page.title.replace(/</g, "‹").replace(/>/g, "›")}” ` : ""}(${page.path})`;
  const version = `Version ${page.sequence}${page.indexing === "unlisted" ? ", unlisted" : ""}`;
  if (page.status === "unchanged") return [[label, `${version}: republished exactly as it is now.`]];
  if (page.status === "added")
    return [[label, page.addedPage
      ? `${version}: a new page of ${page.addedPage.characters.toLocaleString("en-US")} characters${page.addedPage.code ? ", which may hold code" : ""}. Show exact changes to see it in full.`
      : `${version}: a new page.`]];
  if (page.tooLarge) return [[label, `${version}: it changes too much to show every change here.`]];
  return [[label, `${version}: ${exactly(page)}`]];
}
/** What the person asked, in their own messages in this conversation (newest last), quoted as typed; null when unknown. */
export function websitePagesAsked(asked: ReadonlyArray<string> | null | undefined): string | null {
  const said = (asked ?? []).map((one) => one.trim()).filter(Boolean);
  return said.length ? said.map(quote).join(", then ") : null;
}
function exactly(page: WebsitePageRow): string {
  const count = page.edits?.length ?? 0;
  const code = (page.edits ?? []).some((edit) => edit.code);
  // Said both ways (rehearsal run 14): the brief asks whether any change touches the page's code, so "no" is said too.
  const touches = code ? `${count === 1 ? "it changes" : "at least one changes"} the page's code` : `${count === 1 ? "it does not change" : "none changes"} the page's code`;
  return `${count} ${count === 1 ? "change" : "changes"}, ${touches}. Show exact changes to see ${count === 1 ? "it" : "each"} in full.`;
}
const exactChanges = (page: WebsitePageRow) =>
  (page.edits ?? []).map(
    (edit) =>
      `Removes ${quote(edit.removed)} and adds ${quote(edit.added)}${edit.code ? " — this changes the page's code" : ""}.`,
  );
/**
 * W-d729ba9b: every exact character each changed page loses and gains, and whether a change touches its code, as the
 * World compared them: the card's "Show exact changes". Empty when no page changes.
 */
export function websitePagesExact(pages: ReadonlyArray<WebsitePageRow>): string[] {
  // Each piece is quoted as a JSON string, so a page's own quotation marks or line breaks can never end it early.
  const exact = (page: WebsitePageRow) =>
    (page.edits ?? []).map((edit, n) => `Change ${n + 1}${edit.code ? " (touches the page's code)" : ""}: removes ${JSON.stringify(edit.removed)} and adds ${JSON.stringify(edit.added)}.`);
  return [
    ...pages.filter((page) => page.status === "changed" && !page.tooLarge && page.edits?.length).map((page) => `${page.path}:\n${exact(page).join("\n")}`),
    ...pages.filter((page) => page.status === "added" && page.addedPage).map((page) => `${page.path} (a new page):\n${JSON.stringify(page.addedPage!.text)}`),
  ];
}
